import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Heading, Field, Button, Column, Table, Spacer, Select } from '@oliasoft-open-source/react-ui-library';
import { toNum } from '@oliasoft-open-source/units';
import { validateNumber } from 'src/validation/common/validate-number';
import { saveCurrencies, getCurrencies } from 'store/entities/company-settings/company-settings';
import translations from 'src/internationalisation/translation-map.json';
import { useParams } from 'react-router';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';

const Currency = ({ settings, isAdding, currencyOptions, getCurrencies, saveCurrencies }) => {
  const { company } = useParams();
  const { t } = useTranslation();
  const [primaryCurrency, setPrimaryCurrency] = useState(null);
  const [additionalCurrenciesList, setAdditionalCurrenciesList] = useState([]);

  useEffect(() => {
    getCurrencies();
  }, []);

  const mappedCurrencyOptions = currencyOptions.map((c) => ({
    ...c,
    disabled: additionalCurrenciesList.some((cur) => cur.currency === c.value || primaryCurrency === c.value),
  }));

  const primaryCurrencyStore = settings.currencies?.primaryCurrency || null;
  const additionalCurrenciesStore = settings.currencies?.additionalCurrencies || [];

  const isCurrenciesListChanged =
    JSON.stringify(additionalCurrenciesStore) !== JSON.stringify(additionalCurrenciesList) ||
    primaryCurrencyStore !== primaryCurrency;
  const hasErros = additionalCurrenciesList.some((cur) => validateNumber(cur.exchangeRate));

  useEffect(() => {
    setPrimaryCurrency(primaryCurrencyStore);
  }, [settings.currencies?.primaryCurrency]);

  useEffect(() => {
    setAdditionalCurrenciesList(additionalCurrenciesStore);
  }, [settings.currencies?.additionalCurrencies]);

  const onAddCurrency = () =>
    setAdditionalCurrenciesList([
      ...additionalCurrenciesList,
      {
        currency: mappedCurrencyOptions.filter((o) => !o.disabled)[0]?.value,
        exchangeRate: 100,
      },
    ]);

  const onChangeAdditionalCurrency = (ev, i) => {
    const newElement = [...additionalCurrenciesList];
    if (ev.target.name === 'currency') {
      newElement[i] = {
        ...newElement[i],
        currency: ev.target.value,
      };
    } else if (ev.target.name === 'exchangeRate') {
      newElement[i] = {
        ...newElement[i],
        exchangeRate: toNum(ev.target.value),
      };
    }
    setAdditionalCurrenciesList(newElement);
  };

  const onRemoveCurrency = (i) => {
    const newCurrencies = [...additionalCurrenciesList];
    newCurrencies.splice(i, 1);
    setAdditionalCurrenciesList(newCurrencies);
  };

  const onSubmit = () => {
    saveCurrencies(company, primaryCurrency, additionalCurrenciesList, settings);
  };

  return (
    <Column scroll padding spacing={0}>
      <Heading top>{t(translations.currency)}</Heading>
      <>
        <Field label={t(translations.projectSettings_primaryCurrency)}>
          <Select
            name="primaryCurrency"
            options={mappedCurrencyOptions}
            value={primaryCurrency}
            onChange={(e) => {
              setPrimaryCurrency(e.target.value);
            }}
            disabled
          />
        </Field>
        <Table
          table={{
            fixedWidth: '100%',
            columnAlignment: ['left', 'right', 'left'],
            headers: [
              {
                cells: [
                  {
                    value: t(translations.projectSettings_additionalCurrency),
                  },
                  {
                    value: t(translations.projectSettings_exchangeRate),
                    style: { width: '120px' },
                  },
                  { value: '', style: { width: '90px' } },
                ],
                actions: [
                  {
                    primary: true,
                    label: t(translations.add),
                    icon: 'add',
                    disabled: additionalCurrenciesList.length >= 10,
                    onClick: () => {
                      onAddCurrency();
                    },
                  },
                ],
              },
            ],
            rows: additionalCurrenciesList.map((element, i) => ({
              cells: [
                {
                  name: 'currency',
                  type: 'Select',
                  options: mappedCurrencyOptions,
                  value: element.currency,
                  onChange: (ev) => {
                    onChangeAdditionalCurrency(ev, i);
                  },
                  autoLayerWidth: true,
                },
                {
                  name: 'exchangeRate',
                  type: 'Input',
                  error: validateNumber(element.exchangeRate),
                  value: element.exchangeRate,
                  onChange: (ev) => {
                    onChangeAdditionalCurrency(ev, i);
                  },
                },
                {
                  value: `= 1 ${primaryCurrency}`,
                  disabled: true,
                  style: {
                    opacity: '0.5',
                    cursor: 'default',
                  },
                },
              ],
              actions: [
                {
                  label: 'Delete',
                  icon: 'minus',
                  onClick: () => {
                    onRemoveCurrency(i);
                  },
                },
              ],
            })),
          }}
        />
        <Spacer />
        <Button
          label={t(translations.save)}
          onClick={onSubmit}
          disabled={isAdding || !isCurrenciesListChanged || hasErros}
          colored
        />
      </>
    </Column>
  );
};

const mapStateToProps = ({ entities }) => {
  const { isAdding, currenciesList, settings } = entities.companySettings;

  const currencyOptions = currenciesList.map((item) => ({
    label: `(${item.code}) ${item.name}`,
    value: item.code,
  }));

  return { isAdding, settings, currencyOptions };
};

const mapDispatchToProps = { saveCurrencies, getCurrencies };

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(Currency));

export { Container as Currency };
